import { SocialProvider } from "@/mk2/apps/users/lib/socialAuth/types";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";

///////////////////////////////////////////////////////////////////
// Social login trigger — initiated by clicking a social button

export const SOCIAL_LOGIN_TRIGGER = "SOCIAL_LOGIN_TRIGGER";
export interface SocialLoginTriggerAction {
    readonly type: typeof SOCIAL_LOGIN_TRIGGER;
    readonly provider: SocialProvider;
    readonly location: HistoryLocationWithState;
}
export const socialLoginTrigger = (
    provider: SocialProvider,
    location: HistoryLocationWithState,
): SocialLoginTriggerAction => ({
    type: SOCIAL_LOGIN_TRIGGER,
    provider,
    location,
});

///////////////////////////////////////////////////////////////////
// Social login needs profile completion (case c)

export const SOCIAL_LOGIN_NEEDS_COMPLETION = "SOCIAL_LOGIN_NEEDS_COMPLETION";
export interface SocialLoginNeedsCompletionAction {
    readonly type: typeof SOCIAL_LOGIN_NEEDS_COMPLETION;
    readonly provider: SocialProvider;
    readonly prefill: {
        email?: string;
        fullName?: string;
        avatarUrl?: string;
        suggestedUsername?: string;
    };
}

///////////////////////////////////////////////////////////////////
// Social login failure

export const SOCIAL_LOGIN_FAILURE = "SOCIAL_LOGIN_FAILURE";
export interface SocialLoginFailureAction {
    readonly type: typeof SOCIAL_LOGIN_FAILURE;
    readonly provider: SocialProvider;
    readonly error: string;
}

///////////////////////////////////////////////////////////////////
// Complete social signup step 1 — submit username only

export const SOCIAL_COMPLETE_SIGNUP_TRIGGER = "SOCIAL_COMPLETE_SIGNUP_TRIGGER";
export interface SocialCompleteSignupTriggerAction {
    readonly type: typeof SOCIAL_COMPLETE_SIGNUP_TRIGGER;
    readonly formData: {
        username: string;
        captchaToken?: string;
    };
    readonly location: HistoryLocationWithState;
}
export const socialCompleteSignupTrigger = (
    formData: { username: string; captchaToken?: string },
    location: HistoryLocationWithState,
): SocialCompleteSignupTriggerAction => ({
    type: SOCIAL_COMPLETE_SIGNUP_TRIGGER,
    formData,
    location,
});

export const SOCIAL_COMPLETE_SIGNUP_SUCCESS = "SOCIAL_COMPLETE_SIGNUP_SUCCESS";
export interface SocialCompleteSignupSuccessAction {
    readonly type: typeof SOCIAL_COMPLETE_SIGNUP_SUCCESS;
}

export const SOCIAL_COMPLETE_SIGNUP_FAILURE = "SOCIAL_COMPLETE_SIGNUP_FAILURE";
export interface SocialCompleteSignupFailureAction {
    readonly type: typeof SOCIAL_COMPLETE_SIGNUP_FAILURE;
    readonly error: string;
}

///////////////////////////////////////////////////////////////////
// Update optional social profile — step 2

export const SOCIAL_UPDATE_PROFILE_TRIGGER = "SOCIAL_UPDATE_PROFILE_TRIGGER";
export interface SocialUpdateProfileTriggerAction {
    readonly type: typeof SOCIAL_UPDATE_PROFILE_TRIGGER;
    readonly formData: {
        pregnancy_state?: number;
        due_date?: string;
        location_county?: number;
        place?: number;
    };
    readonly location: HistoryLocationWithState;
}
export const socialUpdateProfileTrigger = (
    formData: {
        pregnancy_state?: number;
        due_date?: string;
        location_county?: number;
        place?: number;
    },
    location: HistoryLocationWithState,
): SocialUpdateProfileTriggerAction => ({
    type: SOCIAL_UPDATE_PROFILE_TRIGGER,
    formData,
    location,
});

export const SOCIAL_UPDATE_PROFILE_SUCCESS = "SOCIAL_UPDATE_PROFILE_SUCCESS";
export interface SocialUpdateProfileSuccessAction {
    readonly type: typeof SOCIAL_UPDATE_PROFILE_SUCCESS;
}

export const SOCIAL_UPDATE_PROFILE_FAILURE = "SOCIAL_UPDATE_PROFILE_FAILURE";
export interface SocialUpdateProfileFailureAction {
    readonly type: typeof SOCIAL_UPDATE_PROFILE_FAILURE;
    readonly error: string;
}

///////////////////////////////////////////////////////////////////
// Skip optional social profile — go directly to homepage

export const SOCIAL_SKIP_PROFILE_UPDATE = "SOCIAL_SKIP_PROFILE_UPDATE";
export interface SocialSkipProfileUpdateAction {
    readonly type: typeof SOCIAL_SKIP_PROFILE_UPDATE;
    readonly location: HistoryLocationWithState;
}
export const socialSkipProfileUpdate = (
    location: HistoryLocationWithState,
): SocialSkipProfileUpdateAction => ({
    type: SOCIAL_SKIP_PROFILE_UPDATE,
    location,
});

///////////////////////////////////////////////////////////////////
// Facebook email conflict — needs password to link existing account

export const SOCIAL_LOGIN_NEEDS_PASSWORD = "SOCIAL_LOGIN_NEEDS_PASSWORD";
export interface SocialLoginNeedsPasswordAction {
    readonly type: typeof SOCIAL_LOGIN_NEEDS_PASSWORD;
    readonly provider: SocialProvider;
    readonly email: string;
}

export const SOCIAL_LINK_PASSWORD_TRIGGER = "SOCIAL_LINK_PASSWORD_TRIGGER";
export interface SocialLinkPasswordTriggerAction {
    readonly type: typeof SOCIAL_LINK_PASSWORD_TRIGGER;
    readonly password: string;
    readonly location: HistoryLocationWithState;
}

export const SOCIAL_LINK_PASSWORD_SUCCESS = "SOCIAL_LINK_PASSWORD_SUCCESS";
export interface SocialLinkPasswordSuccessAction {
    readonly type: typeof SOCIAL_LINK_PASSWORD_SUCCESS;
}

export const SOCIAL_LINK_PASSWORD_FAILURE = "SOCIAL_LINK_PASSWORD_FAILURE";
export interface SocialLinkPasswordFailureAction {
    readonly type: typeof SOCIAL_LINK_PASSWORD_FAILURE;
    readonly error: string;
}

export type SocialAuthAction =
    | SocialLoginTriggerAction
    | SocialLoginNeedsCompletionAction
    | SocialLoginNeedsPasswordAction
    | SocialLoginFailureAction
    | SocialCompleteSignupTriggerAction
    | SocialCompleteSignupSuccessAction
    | SocialCompleteSignupFailureAction
    | SocialUpdateProfileTriggerAction
    | SocialUpdateProfileSuccessAction
    | SocialUpdateProfileFailureAction
    | SocialSkipProfileUpdateAction
    | SocialLinkPasswordTriggerAction
    | SocialLinkPasswordSuccessAction
    | SocialLinkPasswordFailureAction;
